import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ClearanceCase, CaseCreate, Customer, Vendor,
  getCases, createCase, deleteCase, getCustomers, getVendors,
} from '../services/clearanceService';
import { getSystemCodesByType, SystemCode } from '../services/systemCodeService';
import { getI18nValue } from '../utils/i18nHelper';
import { usePermission } from '../hooks/usePermission';
import { useMessage } from '../contexts/MessageContext';
import { useFunctionName } from '../hooks/useFunctionName';
import { logView, logCreate, logDelete } from '../utils/userLogHelper';
import { validateSession } from '../utils/sessionValidator';
import FunctionPageHeader from '../components/FunctionPageHeader';
import '../styles/DataTable.css';

const STATUS_COLORS: Record<string, string> = {
  draft:         '#6c757d',
  docs_uploaded: '#0dcaf0',
  parsing:       '#fd7e14',
  parse_failed:  '#dc3545',
  parsed:        '#17a2b8',
  reviewing:     '#6610f2',
  approved:      '#198754',
  xml_ready:     '#0d6efd',
  filed:         '#20c997',
  rejected:      '#dc3545',
};

const STATUS_LABELS: Record<string, string> = {
  draft:         '草稿',
  docs_uploaded: '已上傳',
  parsing:       '解析中',
  parse_failed:  '解析失敗',
  parsed:        '已解析',
  reviewing:     '審核中',
  approved:      '已核准',
  xml_ready:     'XML就緒',
  filed:         '已申報',
  rejected:      '退件',
};

const EMPTY_FORM: CaseCreate = {
  customer_id: 0,
  vendor_id: undefined,
  case_no: '',
  hawb_no: '',
  mawb_no: '',
  ref_no: '',
  transport_mode: '41',
  currency: 'USD',
  etd: '',
  eta: '',
  arrival_date: '',
};

const ClearanceCasesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission, loading: permLoading } = usePermission();
  const { showSuccess, showError, showApiError } = useMessage();
  const pageTitle = useFunctionName('ee_clearance_cases');
  const hasInit = useRef(false);

  const [cases, setCases] = useState<ClearanceCase[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [currencyCodes, setCurrencyCodes] = useState<SystemCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [keyword, setKeyword] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 15;

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<CaseCreate>(EMPTY_FORM);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!permLoading && !hasInit.current) {
      hasInit.current = true;
      const init = async () => {
        const valid = await validateSession('ClearanceCasesPage');
        if (!valid) return;
        await Promise.all([loadCases(), loadCustomers()]);
        const currData = await getSystemCodesByType('CLR_CURRENCY');
        setCurrencyCodes(currData);
        await logView('ee_clearance_cases', undefined, undefined);
      };
      init();
    }
  }, [permLoading]);

  const loadCases = async () => {
    setLoading(true);
    try {
      const data = await getCases();
      setCases(data);
    } catch (err: any) { showApiError(err); }
    finally { setLoading(false); }
  };

  const loadCustomers = async () => {
    try {
      const data = await getCustomers();
      setCustomers(data.filter(c => c.is_active));
    } catch { /* silent */ } // silent ok: background customer list for filter dropdown, non-blocking
  };

  const filtered = cases.filter(c => {
    if (filterStatus && c.status !== filterStatus) return false;
    if (filterCustomer && String(c.customer_id) !== filterCustomer) return false;
    if (keyword.trim()) {
      const kw = keyword.toLowerCase();
      if (
        !c.case_no.toLowerCase().includes(kw) &&
        !(c.hawb_no || '').toLowerCase().includes(kw) &&
        !(c.mawb_no || '').toLowerCase().includes(kw) &&
        !(c.ref_no || '').toLowerCase().includes(kw)
      ) return false;
    }
    return true;
  });

  const totalPages = Math.ceil(filtered.length / perPage);
  const pageData = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.customer_id || !form.case_no) {
      showError('ERR020006', { field: t('clearance.case.requiredFields', '客戶、報單號碼') });
      return;
    }
    setSaving(true);
    try {
      const created = await createCase(form);
      await logCreate('ee_clearance_cases', created);
      showSuccess('SYS020001', { name: pageTitle });
      setShowModal(false);
      setForm(EMPTY_FORM);
      await loadCases();
    } catch (err: any) { showApiError(err); }
    finally { setSaving(false); }
  };

  const handleDelete = async (c: ClearanceCase, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!hasPermission('ee_clearance_cases', 'delete')) return;
    if (!window.confirm(t('clearance.case.confirmDelete', `確定要刪除案件 ${c.case_no} 嗎？`))) return;
    try {
      await deleteCase(c.id);
      await logDelete('ee_clearance_cases', c);
      showSuccess('SYS020003', { name: pageTitle });
      loadCases();
    } catch (err: any) { showApiError(err); }
  };

  const customerName = (id: number) => {
    const c = customers.find(x => x.id === id);
    return c ? `${c.short_code || c.tax_id} ${c.name_zh}` : `#${id}`;
  };

  const set = (k: keyof CaseCreate, v: string | number | undefined) => setForm(f => ({ ...f, [k]: v }));

  const handleCustomerChange = async (customerId: number) => {
    set('customer_id', customerId);
    set('vendor_id', undefined);
    setVendors([]);
    if (customerId) {
      try { setVendors(await getVendors(customerId)); } catch { /* silent */ } // silent ok: optional vendor list, non-blocking
    }
  };

  if (permLoading) return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  if (!hasPermission('ee_clearance_cases', 'read')) return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;

  const canCreate = hasPermission('ee_clearance_cases', 'create');
  const canDelete = hasPermission('ee_clearance_cases', 'delete');

  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="ee_clearance_cases" />
        {canCreate && <button className="btn-primary" onClick={() => { setForm(EMPTY_FORM); setShowModal(true); }}>{t('common.create')}</button>}
      </div>

      {/* 篩選列 */}
      <div className="search-bar" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input value={keyword} onChange={e => { setKeyword(e.target.value); setCurrentPage(1); }}
          placeholder={t('clearance.case.searchPlaceholder', '搜尋案件號、提單號…')}
          style={{ flex: 1, minWidth: 180, padding: '7px 12px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 14 }} />
        <select value={filterCustomer} onChange={e => { setFilterCustomer(e.target.value); setCurrentPage(1); }}
          style={{ padding: '7px 10px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 14 }}>
          <option value="">{t('clearance.case.allCustomers', '所有客戶')}</option>
          {customers.map(c => (
            <option key={c.id} value={String(c.id)}>{c.short_code || c.tax_id} {c.name_zh}</option>
          ))}
        </select>
        <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setCurrentPage(1); }}
          style={{ padding: '7px 10px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 14 }}>
          <option value="">{t('clearance.case.allStatuses', '所有狀態')}</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <button className="btn-secondary" onClick={() => { setKeyword(''); setFilterStatus(''); setFilterCustomer(''); setCurrentPage(1); }}>
          {t('common.reset')}
        </button>
      </div>

      {loading ? <div className="loading">{t('common.loading')}</div> : (
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('clearance.case.caseNo', '報單號碼')}</th>
              <th>{t('clearance.case.customer', '客戶')}</th>
              <th>{t('clearance.case.hawbNo', 'HAWB')}</th>
              <th>{t('clearance.case.mawbNo', 'MAWB')}</th>
              <th>{t('clearance.case.refNo', '參考號')}</th>
              <th>{t('clearance.case.arrivalDate', '到貨日')}</th>
              <th>{t('common.status')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {pageData.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: 24, color: '#6c757d' }}>{t('common.noData')}</td></tr>
            ) : pageData.map(c => (
              <tr key={c.id} style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/ee_clearance_cases/${c.id}`)}>
                <td><strong>{c.case_no}</strong></td>
                <td style={{ fontSize: 12 }}>{customerName(c.customer_id)}</td>
                <td style={{ fontSize: 12 }}>{c.hawb_no}</td>
                <td style={{ fontSize: 12 }}>{c.mawb_no}</td>
                <td style={{ fontSize: 12 }}>{c.ref_no}</td>
                <td style={{ fontSize: 12 }}>{c.arrival_date}</td>
                <td>
                  <span style={badgeStyle(STATUS_COLORS[c.status] || '#6c757d')}>
                    {STATUS_LABELS[c.status] || c.status}
                  </span>
                </td>
                <td onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn-secondary" style={{ padding: '3px 10px', fontSize: 12 }}
                      onClick={() => navigate(`/ee_clearance_cases/${c.id}`)}>
                      {t('common.view')}
                    </button>
                    {canDelete && c.status === 'draft' && (
                      <button className="btn-danger" style={{ padding: '3px 10px', fontSize: 12 }}
                        onClick={e => handleDelete(c, e)}>
                        {t('common.delete')}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* 分頁 */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 16 }}>
          {Array.from({ length: totalPages }, (_, i) => (
            <button key={i} onClick={() => setCurrentPage(i + 1)}
              style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #dee2e6', background: currentPage === i + 1 ? '#0d6efd' : '#fff', color: currentPage === i + 1 ? '#fff' : '#212529', cursor: 'pointer' }}>
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {/* 新增案件 Modal */}
      {showModal && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, maxWidth: 600 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>{t('common.create')} — {pageTitle}</h3>
            <form onSubmit={handleCreate}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label style={labelStyle}>
                  {t('clearance.case.customer', '客戶')} *
                  <select value={form.customer_id || ''} onChange={e => handleCustomerChange(Number(e.target.value))}
                    style={inputStyle} required>
                    <option value="">{t('clearance.case.selectCustomer', '請選擇客戶')}</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>{c.short_code || c.tax_id} {c.name_zh}</option>
                    ))}
                  </select>
                </label>
                <label style={labelStyle}>
                  🏭 外國賣家（選填）
                  <select value={form.vendor_id || ''} onChange={e => set('vendor_id', e.target.value ? Number(e.target.value) : undefined)}
                    style={inputStyle} disabled={!form.customer_id}>
                    <option value="">— 未指定（事後補填）—</option>
                    {vendors.map(v => (
                      <option key={v.id} value={v.id}>{v.name_en}{v.country_code ? ` (${v.country_code})` : ''}</option>
                    ))}
                  </select>
                </label>
                <label style={labelStyle}>
                  {t('clearance.case.caseNo', '報單號碼')} *
                  <input value={form.case_no} onChange={e => set('case_no', e.target.value)}
                    style={inputStyle} required maxLength={50} />
                </label>
                <label style={labelStyle}>
                  {t('clearance.case.hawbNo', 'HAWB 提單號')}
                  <input value={form.hawb_no || ''} onChange={e => set('hawb_no', e.target.value)}
                    style={inputStyle} maxLength={50} />
                </label>
                <label style={labelStyle}>
                  {t('clearance.case.mawbNo', 'MAWB 主提單號')}
                  <input value={form.mawb_no || ''} onChange={e => set('mawb_no', e.target.value)}
                    style={inputStyle} maxLength={50} />
                </label>
                <label style={labelStyle}>
                  {t('clearance.case.refNo', '參考號 / PO')}
                  <input value={form.ref_no || ''} onChange={e => set('ref_no', e.target.value)}
                    style={inputStyle} maxLength={50} />
                </label>
                <label style={labelStyle}>
                  {t('clearance.case.currency', '幣別')}
                  <select value={form.currency || 'USD'} onChange={e => set('currency', e.target.value)}
                    style={inputStyle}>
                    {currencyCodes.map(c => (
                      <option key={c.id} value={c.code}>
                        {c.code} — {getI18nValue(c.code_name, i18n.language)}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={labelStyle}>
                  {t('clearance.case.etd', '出口日 ETD')}
                  <input type="date" value={form.etd || ''} onChange={e => set('etd', e.target.value)}
                    style={inputStyle} />
                </label>
                <label style={labelStyle}>
                  {t('clearance.case.eta', '預計到貨 ETA')}
                  <input type="date" value={form.eta || ''} onChange={e => set('eta', e.target.value)}
                    style={inputStyle} />
                </label>
                <label style={labelStyle}>
                  {t('clearance.case.arrivalDate', '實際到貨日')}
                  <input type="date" value={form.arrival_date || ''} onChange={e => set('arrival_date', e.target.value)}
                    style={inputStyle} />
                </label>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? t('common.saving', '儲存中…') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const badgeStyle = (bg: string): React.CSSProperties => ({
  display: 'inline-block', padding: '2px 8px', borderRadius: 10,
  background: bg, color: '#fff', fontSize: 11, fontWeight: 600,
});
const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};
const modalStyle: React.CSSProperties = {
  background: '#fff', borderRadius: 8, padding: '24px 28px',
  width: '90%', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
};
const labelStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 3, fontSize: 13, fontWeight: 500,
};
const inputStyle: React.CSSProperties = {
  padding: '6px 8px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 13,
};

export default ClearanceCasesPage;
